import React, { useRef, useState } from "react";
import { Flex, Heading, Text, Box, Icon } from "@chakra-ui/react";
import { FiArrowUpRight } from "react-icons/fi";
import RotatingText from "./base/RotatingText";
import LocationBadge from "./base/LocationBadge";
import ScrollIndicator from "./base/ScrollIndicator";
import { accent, roles, greetings } from "../constants/constants";

const slots = [greetings, roles];
// How many full words the active slot cycles through before handing off.
const WORDS_PER_TURN = 2;

const randomSlot = (exclude?: number): number => {
  let next = Math.floor(Math.random() * slots.length);
  while (slots.length > 1 && next === exclude) {
    next = Math.floor(Math.random() * slots.length);
  }
  return next;
};

const fadeUpKeyframes = {
  from: { opacity: 0, transform: "translateY(28px)" },
  to: { opacity: 1, transform: "translateY(0)" },
};

const fadeIn = (delaySeconds: number): Record<string, unknown> => ({
  opacity: 0,
  animation: `homeFadeUp 0.6s cubic-bezier(0.22, 1, 0.36, 1) ${delaySeconds}s forwards`,
  "@keyframes homeFadeUp": fadeUpKeyframes,
});

const Home: React.FC = () => {
  const [activeSlot, setActiveSlot] = useState(() => randomSlot());
  const wordsCompleted = useRef(0);

  const handleBoundary = (): void => {
    wordsCompleted.current += 1;
    if (wordsCompleted.current >= WORDS_PER_TURN) {
      wordsCompleted.current = 0;
      setActiveSlot((prev) => randomSlot(prev));
    }
  };

  return (
    <Box
      as="section"
      id="home"
      aria-labelledby="home-heading"
      position="relative"
      overflow="hidden"
      bg="brand.bg"
    >
      <Flex
        flexDirection="column"
        justifyContent="center"
        alignItems="flex-start"
        paddingX={{ base: "1.5rem", md: "6rem" }}
        minHeight="100vh"
        position="relative"
        zIndex={1}
      >
        <Text
          fontSize={{ base: "md", md: "lg" }}
          fontWeight={600}
          color={accent}
          minHeight="1.6em"
          mb={3}
          sx={fadeIn(0)}
        >
          <RotatingText
            words={greetings}
            active={activeSlot === 0}
            onBoundary={handleBoundary}
            showCursor
          />
        </Text>

        <Heading
          as="h1"
          id="home-heading"
          fontSize={{ base: "2xl", sm: "3xl", md: "4xl", lg: "5xl" }}
          fontWeight={700}
          lineHeight="1.2"
          color="brand.text"
          mb={1}
          sx={fadeIn(0.1)}
        >
          I&apos;m Aman
        </Heading>

        <Text
          fontSize={{ base: "md", md: "xl" }}
          color="brand.muted"
          minHeight="3.4em"
          lineHeight="1.7"
          mb={8}
          maxWidth="500px"
          sx={fadeIn(0.2)}
        >
          <RotatingText
            words={roles}
            active={activeSlot === 1}
            onBoundary={handleBoundary}
            showCursor
          />
        </Text>

        <Box
          as="a"
          href="#experience"
          fontSize="xl"
          fontWeight={700}
          color="brand.text"
          mb={10}
          display="inline-flex"
          alignItems="center"
          gap={2}
          cursor="pointer"
          _hover={{ color: accent }}
          sx={fadeIn(0.3)}
        >
          <Icon
            as={FiArrowUpRight}
            aria-hidden="true"
            color={accent}
            boxSize={5}
          />
          See my work
        </Box>

        <ScrollIndicator />
      </Flex>

      <Box
        position="absolute"
        bottom={{ base: 6, md: 16 }}
        right={{ base: 6, md: 16 }}
        zIndex={1}
        display={{ base: "none", sm: "block" }}
        sx={fadeIn(0.4)}
      >
        <LocationBadge label="Sydney, AUS" />
      </Box>
    </Box>
  );
};

export default Home;
